import { createContext, useContext, type ReactNode } from 'react';

// UI locale for shared components. Everything defaults to English; only the
// internal admin shell provides 'pt-BR'. Merchant, public, login and storefront
// screens never set a locale, so their copy and formatting stay English.

export type UiLocale = 'en' | 'pt-BR';

const UiLocaleContext = createContext<UiLocale>('en');

export function UiLocaleProvider({ locale, children }: { locale: UiLocale; children: ReactNode }) {
  return <UiLocaleContext.Provider value={locale}>{children}</UiLocaleContext.Provider>;
}

export function useUiLocale(): UiLocale {
  return useContext(UiLocaleContext);
}

/** Copy rendered by shared components (states, dialogs, forms, period picker, auto tables). */
const EN = {
  loading: 'Loading…',
  errors: {
    unreachable: 'Server unreachable',
    forbidden: 'Access denied',
    notFound: 'Not available',
    generic: 'Could not load this data',
    network: 'network',
  },
  tryAgain: 'Try again',
  staleData: (message: string) => `Showing the last loaded data. Refresh failed: ${message}`,
  retry: 'Retry',
  closeDialog: 'Close dialog',
  cancel: 'Cancel',
  working: 'Working…',
  optional: 'optional',
  fixErrors: 'Please fix the following',
  dismissNotification: 'Dismiss notification',
  /** Accessible name of an InfoTip button; the topic is the heading or label it explains. */
  moreInfo: (topic?: string) => (topic ? `About ${topic}` : 'More info'),
  period: {
    group: 'Reporting period',
    range: (from: string, to: string) => `${from} → ${to}. Days are counted in UTC.`,
    presets: { today: 'Today', yesterday: 'Yesterday', '7d': '7 days', '30d': '30 days', '90d': '90 days', mtd: 'Month to date' },
    custom: 'Custom',
    from: 'From',
    to: 'To',
    rangeSeparator: 'to',
    apply: 'Apply',
  },
  data: {
    yes: 'Yes',
    no: 'No',
    noData: 'No data',
    noRecords: 'No records.',
    none: 'none',
    items: (n: number) => `${n} item${n === 1 ? '' : 's'}`,
  },
};

export type SharedText = typeof EN;

const PT_BR: SharedText = {
  loading: 'Carregando…',
  errors: {
    unreachable: 'Servidor indisponível',
    forbidden: 'Acesso negado',
    notFound: 'Indisponível',
    generic: 'Não foi possível carregar os dados',
    network: 'rede',
  },
  tryAgain: 'Tentar novamente',
  staleData: (message: string) => `Exibindo os últimos dados carregados. A atualização falhou: ${message}`,
  retry: 'Tentar de novo',
  closeDialog: 'Fechar janela',
  cancel: 'Cancelar',
  working: 'Processando…',
  optional: 'opcional',
  fixErrors: 'Corrija os itens abaixo',
  dismissNotification: 'Fechar notificação',
  moreInfo: (topic?: string) => (topic ? `Sobre: ${topic}` : 'Mais informações'),
  period: {
    group: 'Período do relatório',
    range: (from: string, to: string) => `${from} → ${to}. Dias contados em UTC.`,
    presets: { today: 'Hoje', yesterday: 'Ontem', '7d': '7 dias', '30d': '30 dias', '90d': '90 dias', mtd: 'Mês atual' },
    custom: 'Personalizado',
    from: 'De',
    to: 'Até',
    rangeSeparator: 'a',
    apply: 'Aplicar',
  },
  data: {
    yes: 'Sim',
    no: 'Não',
    noData: 'Sem dados',
    noRecords: 'Nenhum registro.',
    none: 'nenhum',
    items: (n: number) => `${n} ${n === 1 ? 'item' : 'itens'}`,
  },
};

const SHARED_TEXT: Record<UiLocale, SharedText> = { en: EN, 'pt-BR': PT_BR };

export function sharedText(locale: UiLocale): SharedText {
  return SHARED_TEXT[locale];
}

export function useSharedText(): SharedText {
  return SHARED_TEXT[useUiLocale()];
}
